/**
 * Bis die ersten Werte da sind (#75): was in der Zwischenzeit steht und wie lange es dauert.
 *
 * **Die Zeiten sind abgeschrieben** — aus dem Takt des Workers (`apps/worker/src/materialize/
 * cadence.ts`): Eine Viertelstunde wird eine Minute nach ihrem Ende gerechnet, ein Wert vom
 * Anfang der Viertelstunde ist also nach höchstens etwa 16 Minuten sichtbar. Die erste
 * Viertelstunde eines Zählers zählt nicht mit, weil ihr der Stand davor fehlt (#87); der erste
 * Verbrauch ist der der nächsten, nach 16 bis 31 Minuten. Ändert sich der Takt, ändern sich
 * diese Sätze — und ihre Kopie in `apps/mobile/src/lib/first-values.ts`.
 *
 * **Bis dahin stehen die Momentanwerte da.** Sie kommen ohne Verdichtung aus (`conceptNow`, vom
 * Server aus den letzten Werten der Zähler gerechnet) und sind eine Minute nach der ersten
 * Messung da — der Beweis, dass die Einrichtung stimmt, eine halbe Stunde früher.
 */
import { t } from "@hausmeter/i18n";
import type { LiveNow } from "./live-now.js";

/** Im laufenden Betrieb: wie lange ein neuer Wert braucht. */
export const NEW_VALUES_DELAY = "Neue Werte erscheinen spätestens etwa eine Viertelstunde nach der Messung.";

/** Nach dem Einrichten, solange noch kein Zähler gemeldet hat. */
export const FIRST_VALUES_DELAY = "Die ersten Werte erscheinen etwa eine halbe Stunde nach der ersten Messung.";

/** Die Zähler melden, im laufenden Zeitraum ist aber noch nichts gerechnet. */
export const METERS_REPORTING =
  "Die Zähler melden sich. Verbrauch und Erzeugung erscheinen spätestens etwa eine Viertelstunde nach der Messung, nach dem Einrichten beim ersten Mal nach etwa einer halben Stunde.";

/** Die Größen, die bis dahin als Momentanwert stehen — in dieser Reihenfolge. */
export const FIGURE_KEYS = ["consumption", "generation", "grid_import", "grid_export"] as const;

export interface LiveFigure {
  readonly key: (typeof FIGURE_KEYS)[number];
  readonly label: string;
  readonly watts: number;
}

/** Die Momentanwerte, die der Server liefern konnte; was fehlt, fehlt (Regel 8). */
export function liveFigures(values: LiveNow): readonly LiveFigure[] {
  return FIGURE_KEYS.flatMap((key) => {
    const watts = values[key];
    return watts === undefined ? [] : [{ key, label: t(`quantity.${key}`), watts }];
  });
}
